import { useMemo } from 'react';
import { HOME_RANGE_OPTIONS, useBillingRange } from '../../hooks/useBillingRange';
import { useTeamStats } from '../../hooks/useHomeStats';
import { RangeControl } from '../billing/overview/RangeControl';
import { CaseKpiStrip, CaseKpiStripSkeleton } from './CaseKpiStrip';
import { TeamMonitoringPie } from './TeamMonitoringPie';
import { TeamWorkloadCard, TeamWorkloadCardSkeleton } from './TeamWorkloadCard';
import { ALL_TEAMS, buildTeamWorkload, MONITORING_TEAM } from './teamStatsModel';

const RANGE_STORAGE_KEY = 'fv_home_teamRange';
const GRID_CLASSES = 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4';

function LoadFailed({ onRetry }: { onRetry: () => void }) {
    return (
        <div className="flex flex-col items-center justify-center gap-3 py-16">
            <p className="text-sm text-gray-500">Failed to load team workload.</p>
            <button
                type="button"
                onClick={onRetry}
                className="text-xs text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg px-3 py-1.5 transition-colors"
            >
                Retry
            </button>
        </div>
    );
}

export function TeamWorkloadSection() {
    const { range, setRange, rangeLabel } = useBillingRange(
        RANGE_STORAGE_KEY,
        HOME_RANGE_OPTIONS,
        '30D'
    );
    const { stats, isLoading, isFetching, isError, refetch } = useTeamStats(range);
    const workloads = useMemo(
        () => (stats ? ALL_TEAMS.map((team) => buildTeamWorkload(stats, team)) : []),
        [stats]
    );

    return (
        <section className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                <div className="flex items-baseline gap-2 min-w-0">
                    <h2 className="text-lg font-bold text-gray-900">Team workload</h2>
                    <span className="text-sm text-gray-500 truncate">· {rangeLabel}</span>
                    {isFetching && !isLoading && (
                        <span className="text-xs text-gray-400">Refreshing…</span>
                    )}
                    {isError && stats && (
                        <button
                            type="button"
                            onClick={() => refetch()}
                            className="text-xs text-red-500 font-medium hover:text-red-600"
                        >
                            Couldn&apos;t refresh, retry
                        </button>
                    )}
                </div>
                <RangeControl
                    active={range}
                    onChange={setRange}
                    options={HOME_RANGE_OPTIONS}
                    track="white"
                />
            </div>

            {!stats && !isLoading ? (
                <LoadFailed onRetry={() => refetch()} />
            ) : (
                <div className="flex flex-col gap-4">
                    {stats ? (
                        <CaseKpiStrip
                            kpis={stats.caseKpis}
                            conversations={stats.conversationKpis}
                            granularity={stats.bucket}
                        />
                    ) : (
                        <CaseKpiStripSkeleton />
                    )}
                    <div className={GRID_CLASSES}>
                        {stats
                            ? workloads.map((workload) => (
                                  <TeamWorkloadCard
                                      key={workload.team}
                                      workload={workload}
                                      extraPie={
                                          workload.team === MONITORING_TEAM && (
                                              <TeamMonitoringPie monitoring={stats.monitoring} />
                                          )
                                      }
                                  />
                              ))
                            : ALL_TEAMS.map((team) => (
                                  <TeamWorkloadCardSkeleton key={team} team={team} />
                              ))}
                    </div>
                </div>
            )}
        </section>
    );
}
